/* BIRDLINE — Field Set ladder state.
   ------------------------------------------------------------------------
   The ONLY price authority in this theme. Nothing else computes a price, and
   no price string is written into markup, copy or JS anywhere. Every number a
   customer sees comes from window.BL.tiers, which comes from settings_schema.

   Two consumers, one object: the cart drawer and the collection grid both read
   ladderState(), so they cannot disagree about what a tee costs right now.

   priceFor(id)     — what a tier prices at NOW, given the cart as it stands.
   priceIfAdded(id) — what it would price at after one more tap.
   Conflating these two is the most likely bug in this build, so they are named
   for the question they answer rather than for the maths they do.
   ------------------------------------------------------------------------ */
(function () {
  var BL = (window.BL = window.BL || {});

  /* ---- config ---------------------------------------------------------- */

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  var cfg = readJSON(document.getElementById('bl-tier-config')) || {};

  BL.tiers = cfg.tiers || [];
  BL.ladder = cfg.ladder || { shipThreshold: 2, setThreshold: 3, enabled: false };
  BL.strings = cfg.strings || {};
  /* Two rungs, two moments, two settings. Normalised here so callers can ask
     for BL.celebration.ship or .set without guarding for an older config that
     emitted a single string. */
  BL.celebration = (function (c) {
    if (c && typeof c === 'object') {
      return { ship: c.ship || 'confetti', set: c.set || 'birds' };
    }
    return { ship: c || 'confetti', set: c || 'birds' };
  })(cfg.celebration);
  BL.shopAllUrl = cfg.shopAllUrl || '/collections/tees';

  var DEV = /myshopify\.com$/.test(location.hostname) ||
            /(^|[?&])design_mode=/.test(location.search) ||
            location.hostname === '127.0.0.1' ||
            location.hostname === 'localhost';

  /* Exposed so other modules can gate noisy diagnostics on the same test
     rather than each inventing its own idea of what a preview is. */
  BL.dev = DEV;

  function warn() {
    if (!DEV || !window.console || !console.warn) return;
    console.warn.apply(console, ['[BL ladder]'].concat([].slice.call(arguments)));
  }

  BL.tier = function (id) {
    if (!id) return null;
    var key = String(id).toLowerCase();
    for (var i = 0; i < BL.tiers.length; i++) {
      if (BL.tiers[i].id === key) return BL.tiers[i];
    }
    return null;
  };

  /* ---- money ----------------------------------------------------------- */

  /* A whole-dollar amount prints without cents; anything else prints both
     places. The price line and the shelf price both come out of this one
     function so they can never drift apart. */
  BL.money = function (amount) {
    var n = Number(amount);
    if (!isFinite(n)) return '';
    var whole = Math.round(n * 100) % 100 === 0;
    return '$' + (whole ? String(Math.round(n)) : n.toFixed(2));
  };

  BL.moneyCents = function (cents) {
    var n = Number(cents);
    if (!isFinite(n)) return '';
    return BL.money(n / 100);
  };

  /* ---- the Field Set reduction ----------------------------------------- */

  /* THE rule, and the only arithmetic in this build that touches a price.

     Field Set is a fixed amount off each tee, never a percentage and never a
     flat replacement price. That distinction is worth money: extended sizes
     carry an upcharge, so a flat Field Set price would give away the whole
     upcharge on a 4XL to save the agreed amount on a small. Subtracting instead
     means every unit gives up exactly the same amount and the upcharge
     survives — and on the current ladder every size lands on a whole dollar.

     `units` is how many tees the amount covers, so the same function prices a
     single shelf price (1) and a cart line showing a quantity total (n). */
  BL.reduction = typeof cfg.reductionCents === 'number' ? cfg.reductionCents : 595;

  BL.setPriceCents = function (fullCents, units) {
    var full = Number(fullCents);
    if (!isFinite(full)) return null;
    var n = Number(units);
    if (!isFinite(n) || n < 1) n = 1;
    /* Never let the reduction push a price through zero. A tee cheaper than
       the reduction would be a data error, not a free tee. */
      return Math.max(0, Math.floor((full - (BL.reduction * n)) / 100) * 100);
  };

  /* ---- the cart, resolved to tiers ------------------------------------- */

  /* Liquid resolves tiers, not JavaScript: /cart.js does not return product
     tags, so JS on its own cannot tell a Signature tee from an Everyday one.
     bl-tier-cart.liquid renders the authoritative map inside the drawer
     section, which re-renders on every cart mutation. Always read the LAST map
     in the DOM — after a drawer refresh that is the fresh one. */
  function tierMap() {
    var nodes = document.querySelectorAll('[data-bl-tier-map]');
    var map = readJSON(nodes[nodes.length - 1]);
    if (!map) return { tees: 0, untagged: 0, lines: [], itemCount: 0, totalPrice: 0 };
    return map;
  }
  BL.tierMap = tierMap;

  /* tierOf(line) takes either a Liquid map line or a /cart.js item and answers
     with a tier id. It matches on variant, then product, then handle, because
     a /cart.js item and a Liquid line describe the same thing with different
     field names. */
  BL.tierOf = function (item, map) {
    if (!item) return null;
    var lines = (map || tierMap()).lines || [];
    var vid = item.variant_id || item.id;
    var pid = item.product_id;
    var handle = item.handle;
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      if (vid && l.variant_id === vid) return l.tier;
      if (pid && l.product_id === pid) return l.tier;
      if (handle && l.handle === handle) return l.tier;
    }
    return null;
  };

  /* ---- the state ------------------------------------------------------- */

  /* One function, one output, and it is the only thing any renderer reads. */
  BL.ladderState = function (cart) {
    var L = BL.ladder;
    var map = tierMap();

    /* Prefer the Liquid count. Re-derive from the lines as a check: if the two
       disagree the map is malformed, which is a bug worth shouting about
       rather than quietly pricing something wrong. */
    var derived = 0;
    var byTier = {};
    (map.lines || []).forEach(function (l) {
      derived += l.quantity;
      byTier[l.tier] = (byTier[l.tier] || 0) + l.quantity;
    });
    var tees = typeof map.tees === 'number' ? map.tees : derived;
    if (tees !== derived) {
      warn('tier map disagrees with itself:', map.tees, 'vs', derived);
      tees = derived;
    }

    /* A cart passed in from /cart.js wins on quantities — it is fresher than
       any rendered map on a page that has not re-rendered yet. Lines the map
       does not know are not tees and do not count toward either threshold. */
    if (cart && cart.items) {
      var live = 0;
      var liveByTier = {};
      cart.items.forEach(function (item) {
        var t = BL.tierOf(item, map);
        if (!t) return;
        live += item.quantity;
        liveByTier[t] = (liveByTier[t] || 0) + item.quantity;
      });
      tees = live;
      byTier = liveByTier;
    }

    if (map.untagged) {
      warn(map.untagged + ' cart line(s) resolved to a tier by product type rather ' +
           'than a tier: tag. Tag them tier:signature / tier:everyday — until then ' +
           'they price at full price only.');
    }

    var enabled = L.enabled !== false && BL.tiers.length > 0;

    function priceAt(tierId, n) {
      var t = BL.tier(tierId);
      if (!t) return null;
      if (!enabled) return t.full;
      return n >= L.setThreshold ? t.set : t.full;
    }

    return {
      enabled: enabled,
      tees: tees,
      byTier: byTier,
      shipUnlocked: enabled && tees >= L.shipThreshold,
      setUnlocked: enabled && tees >= L.setThreshold,
      pct: Math.min(tees / L.shipThreshold, 1) * 100,
      shipRemaining: Math.max(L.shipThreshold - tees, 0),
      setRemaining: Math.max(L.setThreshold - tees, 0),
      priceFor: function (id) { return priceAt(id, tees); },
      priceIfAdded: function (id) { return priceAt(id, tees + 1); }
    };
  };

  /* ---- generated copy -------------------------------------------------- */

  function fill(template, vars) {
    return String(template || '').replace(/\{\{(\w+)\}\}/g, function (_, key) {
      return vars[key] === undefined ? '' : String(vars[key]);
    });
  }
  BL.fill = fill;

  /* "<label> <price> · <label> <price>" — mapped over the configured tiers and
     joined, never typed. A third tier changes a config entry and nothing else. */
  BL.priceline = function (state, mode) {
    var st = state || BL.ladderState();
    var get = mode === 'ifAdded' ? st.priceIfAdded : st.priceFor;
    var item = BL.strings.pricelineItem || '{{label}} {{price}}';
    var join = BL.strings.pricelineJoin || ' · ';
    return BL.tiers.map(function (t) {
      return fill(item, { label: t.label, price: BL.money(get(t.id)) });
    }).join(join);
  };

  /* The three meter states from the brief, generated from locales. */
  BL.meterCopy = function (state) {
    var st = state || BL.ladderState();
    var s = BL.strings;
    var n;
    if (!st.shipUnlocked) {
      n = st.shipRemaining;
      return {
        big: fill(s.shipLockedBig, { n: BL.ladder.shipThreshold }),
        lead: fill(n === 1 ? s.shipLockedLeadOne : s.shipLockedLead, { k: n })
      };
    }
    if (!st.setUnlocked) {
      n = st.setRemaining;
      return {
        big: fill(s.shipUnlockedBig, {}),
        lead: fill(n === 1 ? s.shipUnlockedLeadOne : s.shipUnlockedLead, { k: n })
      };
    }
    return {
      big: fill(s.setUnlockedBig, {}),
      lead: fill(s.setUnlockedLead, { priceline: BL.priceline(st) })
    };
  };

  /* ---- analytics ------------------------------------------------------- */

  /* Detect what this store actually has rather than assuming gtag. */
  BL.track = function (name, params) {
    var payload = params || {};
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, payload));
      }
    } catch (e) { /* analytics must never break the cart */ }
    document.dispatchEvent(new CustomEvent('bl:track', { detail: { name: name, params: payload } }));
  };

  /* ---- live cart ------------------------------------------------------- */

  /* Collection templates have no drawer state of their own until something is
     added, so read /cart.js once on load. This is what stops the grid and the
     drawer from ever disagreeing. */
  BL.refresh = function () {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        BL.cart = cart;
        if (BL.recall) BL.recall.observe(cart);
        BL.state = BL.ladderState(cart);
        document.dispatchEvent(new CustomEvent('bl:ladder', { detail: BL.state }));
        return BL.state;
      })
      .catch(function () {
        BL.state = BL.ladderState();
        return BL.state;
      });
  };

  /* ---- cart recall --------------------------------------------------------
  Shopify's own cart cookie already survives a browser restart -- around a month
  as of this writing -- so most people who come back later the same day find
  their tees waiting without any help from us. This is for the cases the cookie
  does not cover: site data cleared, a private window, a browser that evicted
  the cookie early, a phone that wiped it overnight.

  We mirror the cart into localStorage, which is emptied by a different set of
  actions than cookies are, and offer it back when the live cart is empty but
  the mirror is not.

  Two things must never trigger the offer, because either would be asking
  someone to re-add tees they deliberately do not have:

    - a completed order. The checkout button spends the mirror on the way out.
    - a cart the shopper emptied themselves. If the count falls to zero while we
      are watching, that was them, and the mirror is dropped.

  What is left is the case worth catching: a cart that was full when the tab
  closed and empty when it opened again.
  ------------------------------------------------------------------------- */

  var SNAP_KEY = 'bl:cart:snapshot';
  var SEEN_KEY = 'bl:cart:seen';
  var SNAP_MAX_AGE = 30 * 24 * 60 * 60 * 1000;

  function snapDrop() {
    try { localStorage.removeItem(SNAP_KEY); } catch (e) {}
  }

  function snapRead() {
    try {
      var raw = localStorage.getItem(SNAP_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || s.v !== 1 || !s.items || !s.items.length) return null;
      if (Date.now() - Number(s.ts) > SNAP_MAX_AGE) { snapDrop(); return null; }
      return s;
    } catch (e) { return null; }
  }

  /* Has this browsing session already watched a cart with something in it? */
  function sawCart(set) {
    try {
      if (set) sessionStorage.setItem(SEEN_KEY, '1');
      return sessionStorage.getItem(SEEN_KEY) === '1';
    } catch (e) { return false; }
  }

  function snapWrite(cart) {
    if (!cart || !cart.items) return;
    var items = [];
    cart.items.forEach(function (i) {
      if (!i.id || !(i.quantity > 0)) return;
      items.push({
        id: i.id,
        qty: i.quantity,
        title: i.product_title || i.title || '',
        variant: i.variant_title || '',
        url: i.url || '',
        img: i.image || ''
      });
    });
    if (!items.length) return;
    try {
      localStorage.setItem(SNAP_KEY, JSON.stringify({
        v: 1, ts: Date.now(), count: cart.item_count, items: items
      }));
    } catch (e) {}
  }

  BL.recall = {
    observe: function (cart) {
      if (cart && cart.item_count > 0) {
        snapWrite(cart);
        sawCart(true);
        return;
      }
      if (sawCart()) snapDrop();
    },

    /* The cart the shopper could have back, or null if there is nothing to
       offer or offering would be wrong. */
    pending: function () {
      if (BL.cart && BL.cart.item_count > 0) return null;
      if (sawCart()) return null;
      return snapRead();
    },

    spend: snapDrop,

    restore: function () {
      var snap = snapRead();
      if (!snap) return Promise.resolve({ added: 0, missing: [] });
      /* One line at a time. A variant that sold out in the meantime should
         cost us that line and nothing else -- a single /cart/add.js carrying
         every item fails as a whole and the shopper gets nothing back. */
      var added = 0, missing = [];
      var chain = Promise.resolve();
      snap.items.forEach(function (it) {
        chain = chain.then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: it.id, quantity: it.qty }] })
          }).then(function (r) {
            if (r.ok) added += 1;
            else missing.push(it);
          }).catch(function () { missing.push(it); });
        });
      });
      return chain.then(function () {
        snapDrop();
        sawCart(true);
        return { added: added, missing: missing };
      });
    }
  };

  /* Leaving for checkout means these tees are about to stop being a cart.
     Captured on the way down so it still runs when the button submits. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('[name="checkout"], [data-bl-checkout], a[href^="/checkout"], a[href*="/checkouts/"]')) {
      snapDrop();
      sawCart(true);
    }
  }, true);

  /* ---- size memory -------------------------------------------------------
  Quick View opened on whatever size the product listed first, so a shopper
     working through a collection in Large re-picked Large on every card. Two
     sources, most to least deliberate:
       1. the size they last chose by hand this session
       2. the size they have actually been buying -- the most common size
          across the tees already in the cart, ties broken toward the line
          added most recently
     Both are HINTS. Every caller has to fall back to its own default when the
     remembered size is not offered, or is not buyable, on the product in front
     of the shopper. Session storage, not local: a size is a shopping decision,
     not a preference worth remembering next week. */
  var SIZE_KEY = 'bl:size';

  BL.rememberSize = function (value) {
    if (!value) return;
    try { sessionStorage.setItem(SIZE_KEY, String(value)); } catch (e) {}
  };

  BL.sizeMemory = function () {
    var picked = null;
    try { picked = sessionStorage.getItem(SIZE_KEY); } catch (e) {}
    if (picked) return picked;

    var cart = BL.cart;
    if (!cart || !cart.items || !cart.items.length) return null;

    /* Count only tier-eligible lines when the map is available: a hat in the
       cart has sizes too, and they are not the same ladder of sizes. */
    var map = null;
    try { map = BL.tierMap(); } catch (e) {}
    var eligible = null;
    if (map && map.lines && map.lines.length) {
      eligible = {};
      map.lines.forEach(function (l) { eligible[l.key] = true; });
    }

    var counts = {};
    var best = null;
    var bestN = 0;
    cart.items.forEach(function (item) {
      if (eligible && !eligible[item.key]) return;
      var opts = item.options_with_values || [];
      for (var i = 0; i < opts.length; i++) {
        if (String(opts[i].name).toLowerCase() !== 'size') continue;
        var v = opts[i].value;
        if (!v) continue;
        counts[v] = (counts[v] || 0) + (item.quantity || 1);
        /* >= rather than > so a tie lands on the later line, which is the
           one added most recently. */
        if (counts[v] >= bestN) { bestN = counts[v]; best = v; }
      }
    });
    return best;
  };

  BL.state = BL.ladderState();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', BL.refresh);
  } else {
    BL.refresh();
  }
})();
